import React, { useState, useMemo } from "react";
import {
  Table,
  Header,
  TextFilter,
  Pagination,
  SpaceBetween,
  ButtonDropdown,
  Badge,
} from "@cloudscape-design/components";
import ButtonDropDown from "../organizations/ManageUserRolesMenu";
import { MembersTableProps } from "types/Members";
import { useParams } from "react-router-dom";
import TablePreferences from "../Layout/TablePreferences";
import { PreferencesType } from "types/types";
import organizationService from "services/organizationService";
import { useNotification } from "contexts/NotificationContext";
import DeleteConfirmationModal from "../shared/DeleteConfirmationModal";
import UserService from "services/UserService";
import PERMISSIONS from "configs/Permissions";

const MembersTable: React.FC<
  MembersTableProps & {
    connectedUserId: string;
    onMemberDeleted: () => void;
  }
> = ({ members, connectedUserId, onMemberDeleted }) => {
  const { addNotification } = useNotification();
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [filteringText, setFilteringText] = useState("");
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<string | null>(null);
  const [isDeletingMember, setIsDeletingMember] = useState(false);

  const handleDeleteMember = async (memberId: string) => {
    setMemberToDelete(memberId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete || !id) return;
    setIsDeletingMember(true);

    try {
      await organizationService.deleteMember(id, memberToDelete);
      addNotification("success", "Member deleted successfully");
      setShowDeleteModal(false);
      onMemberDeleted();
    } catch (error) {
      addNotification("error", "Failed to delete member");
    } finally {
      setIsDeletingMember(false);
    }
  };

  type Role = { id: string; name: string };
  const separateUserRoles = (userRoles: Role[] | undefined) => {
    if (!userRoles || !Array.isArray(userRoles)) {
      return {
        manageRoles: [],
        viewRoles: [],
      };
    }
    return {
      manageRoles: userRoles
        .filter((role) => role.name?.startsWith("manage-"))
        .map((role) => ({ id: role.id, text: role.name })),
      viewRoles: userRoles
        .filter((role) => role.name?.startsWith("view-"))
        .map((role) => ({ id: role.id, text: role.name })),
    };
  };

  const filteredMembers = useMemo(() => {
    return members.filter((member) =>
      member.email.toLowerCase().includes(filteringText.toLowerCase())
    );
  }, [members, filteringText]);

  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredMembers
      .slice(startIndex, startIndex + itemsPerPage)
      .map((member) => ({
        ...member,
        isOwner: member.roles?.some(
          (role: Role) => role.name?.trim().toLowerCase() === "owner"
        ),
      }));
  }, [filteredMembers, currentPage, itemsPerPage]);


  const columnOptions = [
    { id: "email", label: "Email", visible: true, alwaysVisible: true },
    { id: "roles", label: "Roles", visible: true, alwaysVisible: true },
    { id: "actions", label: "Actions", visible: true, alwaysVisible: true },
  ];

  const pageSizeOptions = [
    { value: 10, label: "10 members" },
    { value: 20, label: "20 members" },
    { value: 50, label: "50 members" },
    { value: 100, label: "100 members" },
  ];

  const [preferences, setPreferences] = useState<PreferencesType>({
    pageSize: 10,
    wrapLines: false,
    stripedRows: true,
    contentDensity: "comfortable",
    contentDisplay: columnOptions.map((col) => ({
      id: col.id,
      visible: true,
      disabled: true,
    })),
    stickyColumns: { first: 0, last: 0 },
  });
  const columnDisplay = useMemo(() => {
    return preferences.contentDisplay.map((column) => ({
      ...column,
      visible: column.visible,
    }));
  }, [preferences.contentDisplay]);

  const handlePreferencesChange = (detail: any) => {
    setPreferences((prev) => ({
      ...prev,
      ...detail,
      pageSize: detail.pageSize ?? prev.pageSize,
      wrapLines: detail.wrapLines ?? prev.wrapLines,
      stripedRows: detail.stripedRows ?? prev.stripedRows,
      contentDensity: detail.contentDensity ?? prev.contentDensity,
      stickyColumns: {
        first: detail.stickyColumns?.first ?? prev.stickyColumns.first,
        last: detail.stickyColumns?.last ?? prev.stickyColumns.last,
      },
      contentDisplay: detail.contentDisplay ?? prev.contentDisplay,
    }));
  };


  return (
    <>
      <DeleteConfirmationModal
        isVisible={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeletingMember}
        headerText="Delete Member"
        deleteConfirmationValue={"delete member"}
      >
        Are you sure you want to delete this member? This action cannot be
        undone.
      </DeleteConfirmationModal>
      <Table
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        className="members-table"
        columnDefinitions={[
          {
            id: "email",
            header: "Email",
            cell: (item) => (
              <div className="owner-badge-container">
                <span>{item.email}</span>
                {item.isOwner && <Badge color="blue">Owner</Badge>}
              </div>
            ),
            width: 250,
          },
          {
            id: "roles",
            header: "Roles",
            cell: (item) => {
              const { manageRoles, viewRoles } = separateUserRoles(item.roles);
              return (
                <div className="roles-dropdown-in-members-table">
                  <div className="roles-dropdown-in-members-table">
                    <ButtonDropDown type="manage" roles={manageRoles} />
                    <ButtonDropDown type="view" roles={viewRoles} />
                  </div>
                </div>
              );
            },
            width: 300,
          },
          ...(UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS])
            ? [
              {
                id: "actions",
                header: "Actions",
                cell: (item: any) => {
                  const dropdownItems = [
                    {
                      text: "Edit roles",
                      id: "edit",
                      href: `/organizations/${id}/edit-roles/${item.id}`,
                      disabled: item.id === connectedUserId || item.isOwner,
                    },
                    {
                      text: "Delete",
                      id: "delete",
                      disabled: item.id === connectedUserId || item.isOwner,
                    },
                  ];

                  return (
                    <ButtonDropdown
                      onItemClick={({ detail }) => {
                        if (detail.id === "delete") {
                          handleDeleteMember(item.id);
                        }
                      }}
                      items={dropdownItems}
                      expandToViewport
                      variant="icon"
                      ariaLabel="Actions"
                    />
                  );
                },
                width: 50,
              },
            ]
            : []),


        ]}
        items={paginatedMembers}
        columnDisplay={preferences.contentDisplay}
        stickyColumns={preferences.stickyColumns}
        wrapLines={preferences.wrapLines}
        stripedRows={preferences.stripedRows}
        contentDensity={
          preferences.contentDensity === "compact" ? "compact" : "comfortable"
        }
        enableKeyboardNavigation
        variant="container"
        filter={
          <TextFilter
            filteringPlaceholder="Find members"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPage(1);
            }}
            countText={`${filteredMembers.length} matches`}
          />
        }
        header={<Header>Members</Header>}
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            pagesCount={Math.max(
              1,
              Math.ceil(filteredMembers.length / itemsPerPage)
            )}
          />
        }
        preferences={
          <TablePreferences
            preferences={preferences}
            setPreferences={handlePreferencesChange}
            setCurrentPage={setCurrentPage}
            columnOptions={columnOptions}
            pageSizeOptions={pageSizeOptions}
          />
        }
      />
    </>
  );
};

export default MembersTable;
